import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import { PlusCircle, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const OTHER_GROUP_VALUE = "__other__";

function SuperSchooladminManagement() {
  const [list, setList] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    username: "",
    password: "",
    firstName: "",
    lastName: "",
    email: "",
    schoolGroup: "",
    schoolGroupOther: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchGroups = async () => {
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.schoolGroups);
      setGroups(res.data?.data?.groups ?? []);
    } catch {
      setGroups([]);
    }
  };

  const fetchList = async () => {
    try {
      setError("");
      const res = await axiosInstance.get(API_ENDPOINTS.superSchoolAdmins);
      setList(res.data?.data?.superSchoolAdmins ?? []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load Super Customer Admins");
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  useEffect(() => {
    if (modalOpen) fetchGroups();
  }, [modalOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!form.username?.trim()) {
      setError("Username is required");
      return;
    }
    if (!form.password || form.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (!form.firstName?.trim()) {
      setError("First name is required");
      return;
    }
    const groupValue = form.schoolGroup === OTHER_GROUP_VALUE
      ? form.schoolGroupOther?.trim()
      : form.schoolGroup?.trim();
    if (!groupValue) {
      setError("Customer group is required. Select from dropdown or type a new one.");
      return;
    }
    setSubmitting(true);
    try {
      await axiosInstance.post(API_ENDPOINTS.superSchoolAdmins, {
        username: form.username.trim(),
        password: form.password,
        firstName: form.firstName.trim(),
        lastName: form.lastName?.trim() || "",
        email: form.email?.trim() || "",
        schoolGroup: groupValue.toLowerCase(),
      });
      setSuccess("Super Customer Admin created. They can log in with the username and password you set.");
      setForm({
        username: "",
        password: "",
        firstName: "",
        lastName: "",
        email: "",
        schoolGroup: "",
        schoolGroupOther: "",
      });
      setModalOpen(false);
      fetchList();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create Super Customer Admin");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Super Customer Admins</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Create and manage group admins who can view all customers in their group (e.g. DPS branches).
          </p>
        </div>
        <Button onClick={() => setModalOpen(true)}>
          <PlusCircle className="h-5 w-5 mr-2" />
          Add Super Customer Admin
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="border-green-200 bg-green-50 text-green-800">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <p className="text-muted-foreground py-8">Loading...</p>
      ) : list.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <UserCog className="h-16 w-16 text-gray-300 mb-4" />
            <p className="text-muted-foreground mb-4">No Super Customer Admins yet.</p>
            <Button onClick={() => setModalOpen(true)}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Super Customer Admin
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Customer Group</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((admin) => (
                <TableRow key={admin._id}>
                  <TableCell className="font-medium">
                    {[admin.firstName, admin.lastName].filter(Boolean).join(" ") || "—"}
                  </TableCell>
                  <TableCell>{admin.username}</TableCell>
                  <TableCell>{admin.email || "—"}</TableCell>
                  <TableCell>{admin.schoolGroup || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Super Customer Admin</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username">Username *</Label>
              <Input
                id="username"
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                placeholder="e.g. dpsadmin"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Password * (min 6 characters)</Label>
              <Input
                id="password"
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="••••••••"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="firstName">First name *</Label>
              <Input
                id="firstName"
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                placeholder="e.g. DPS"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last name</Label>
              <Input
                id="lastName"
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                placeholder="e.g. Group Admin"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="admin@example.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Customer group *</Label>
              <Select
                value={form.schoolGroup || (form.schoolGroupOther ? OTHER_GROUP_VALUE : "")}
                onValueChange={(v) => setForm((f) => ({ ...f, schoolGroup: v, schoolGroupOther: v === OTHER_GROUP_VALUE ? f.schoolGroupOther : "" }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select group or add new" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))}
                  <SelectItem value={OTHER_GROUP_VALUE}>Other (type below)</SelectItem>
                </SelectContent>
              </Select>
              {form.schoolGroup === OTHER_GROUP_VALUE && (
                <Input
                  value={form.schoolGroupOther}
                  onChange={(e) => setForm((f) => ({ ...f, schoolGroupOther: e.target.value }))}
                  placeholder="e.g. dps (same as group on customers)"
                  className="mt-2"
                />
              )}
              <p className="text-xs text-muted-foreground mt-1">
                They will only see customers that have this group set (e.g. all DPS branches with group &quot;dps&quot;).
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Creating..." : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SuperSchooladminManagement;
